<template>
    <div>
        <button
            v-if="showTrigger"
            v-show="!isAutoOpened"
            type="button"
            :class="buttonClass"
            data-bs-toggle="modal"
            :data-bs-target="'#' + id"
            :data-oevent-category="actionTms.category"
            :data-oevent-action="actionTms.action"
            :data-oevent-label="actionTms.label"
            :aria-label="ariaLabel"
        >
            <span
                v-if="isIconButton"
                class="icon"
                :class="iconName"
                aria-hidden="true"
            />
            <template v-else>
                {{ actionLabel }}
            </template>
        </button>
        <div
            :id="id"
            ref="modal"
            class="modal fade"
            :class="modalClass"
            tabindex="-1"
            role="dialog"
            :aria-labelledby="labelId"
            aria-hidden="true"
            data-ob1-component="Popin"
        >
            <div class="modal-dialog" role="document">
                <div class="modal-content py-0 px-1 px-md-2">
                    <div class="modal-header">
                        <slot name="header" />
                        <button
                            type="button"
                            class="btn-close"
                            data-bs-dismiss="modal"
                            :aria-label="modalCloseLabel"
                        >
                            <span class="icon icon-delete" aria-hidden="true" />
                        </button>
                    </div>

                    <div v-if="$slots.default" class="modal-body" :class="bodyClass">
                        <slot />
                    </div>

                    <div v-else-if="imageUrl" class="modal-body">
                        <div class="ob1-modal-body-visual">
                            <div class="ob1-modal-img">
                                <img :src="imageUrl" alt="">
                            </div>
                            <div class="ob1-modal-content">
                                <h4 v-if="modalTitle" :id="labelId" v-html="modalTitle" />
                                <p>
                                    {{ modalContent }}
                                </p>
                                <div class="ob1-modal-footer-visual">
                                    <button class="btn btn-secondary m-0" type="button">
                                        {{ modalButtonLabel }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else class="modal-body">
                        <h4 v-if="modalTitle" :id="labelId" v-html="modalTitle" />
                        <p v-if="modalContent">
                            {{ modalContent }}
                        </p>

                        <div v-if="alertHtmlContent" class="alert-container additional-message align-items-center" v-html="alertHtmlContent" />
                        <div v-if="htmlContent" class="mt-1" v-html="htmlContent" />
                    </div>
                    <div v-if="hideFooter" />
                    <div v-else-if="$slots.footer" class="modal-footer d-flex justify-content-center bg-white py-2">
                        <slot name="footer" />
                    </div>
                    <div v-else class="modal-footer d-inline-flex flex-column flex-md-row-reverse justify-content-center">
                        <button
                            v-if="isModalButtonPrimary"
                            v-bind="isModalButtonClose ? { 'data-bs-dismiss': 'modal' } : {}"
                            class="btn btn-primary mx-0 mb-2 ms-md-2 mb-md-0"
                            type="button"
                        >
                            {{ modalButtonLabel }}
                        </button>
                        <button
                            v-else
                            v-bind="isModalButtonClose ? { 'data-bs-dismiss': 'modal' } : {}"
                            class="btn btn-secondary m-0"
                            type="button"
                        >
                            {{ modalButtonLabel }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {TmsGaDto} from "../../ob1/dto/TmsGaDto";

export default defineComponent({
    name: "OverlayDialog",
    props: {
        // id de l'modal
        id: {
            type: String,
            required: true
        },
        actionLabel: {
            type: String,
            required: false,
            default: ""
        },
        // false quand la popin est ouverte depuis l'extérieur (data-bs-target)
        showTrigger: {
            type: Boolean,
            required: false,
            default: true
        },
        hideFooter: {
            type: Boolean,
            required: false,
            default: false
        },
        modalClass: {
            type: String,
            required: false,
            default: ""
        },
        bodyClass: {
            type: String,
            required: false,
            default: ""
        },
        modalCloseLabel: {
            type: String,
            required: false,
            default: "Fermer"
        },
        modalTitle: {
            type: String,
            required: false,
            default: null
        },
        modalContent: {
            type: String,
            required: false,
            default: null
        },
        isModalButtonClose: {
            type: Boolean,
            required: false,
            default: true
        },
        modalButtonLabel: {
            type: String,
            required: false,
            default: "Fermer"
        },
        isModalButtonPrimary: {
            type: Boolean,
            required: false,
            default: true
        },
        isActionButton: {
            type: Boolean,
            required: false,
            default: true
        },
        imageUrl: {
            type: String,
            required: false,
            default: null
        },
        alertHtmlContent: {
            type: String,
            required: false,
            default: null
        },
        htmlContent: {
            type: String,
            required: false,
            default: null
        },
        tmsCategory: {
            type: String,
            required: false,
            default: ""
        },
        tmsLabel: {
            type: String,
            required: false,
            default: "ouverture-popin"
        },
        isAutoOpened: {
            type: Boolean,
            required: false,
            default: false
        },
        isIconButton: {
            type: Boolean,
            required: false,
            default: false
        },
        iconName: {
            type: String,
            required: false,
            default: ""
        },
        ariaLabel: {
            type: String,
            required: false,
            default: ""
        }
    },
    computed: {
        labelId() {
            return this.id + '-label'
        },
        actionTms(): TmsGaDto {
            return {
                category: this.tmsCategory,
                action: toBroadbandTmsFormat(this.actionLabel),
                label: toBroadbandTmsFormat(this.tmsLabel)
            }
        },
        buttonClass() {
            if (this.isActionButton) return 'btn btn-primary';
            if (this.isIconButton) return 'ob1-link-icon';
            return 'ob1-link-arrow';
        }
    },
    watch: {
        isAutoOpened(newIsAutoOpened, _) {
            this.openModal(newIsAutoOpened);
        }
    },
    mounted() {
        this.getModalElement()?.addEventListener('hide.bs.modal', this.blurFocusedElement);
        this.$nextTick(() => {
            this.openModal(this.isAutoOpened);
        });
    },
    beforeDestroy() {
        this.getModalElement()?.removeEventListener('hide.bs.modal', this.blurFocusedElement);
    },
    methods: {
        getModalElement(): HTMLElement | null {
            return (this.$refs.modal as HTMLElement | undefined) ?? null;
        },
        // Bootstrap pose aria-hidden à la fermeture : on retire le focus avant pour éviter le warning d'accessibilité
        blurFocusedElement() {
            const activeElement = document.activeElement as HTMLElement | null;
            if (activeElement && this.getModalElement()?.contains(activeElement)) {
                activeElement.blur();
            }
        },
        openModal(isAutoOpened: boolean) {
            if (isAutoOpened) {
                const modalButtonOpener = this.$el.querySelector('[data-bs-toggle="modal"]') as HTMLElement | null;
                if (modalButtonOpener) modalButtonOpener.click();
            }
        }
    }
})
</script>

<style scoped lang="scss">
@import 'ob1_variables';

// Soustraction d'un px afin d'éviter que les écrans 736px soient couverts aussi bien par le max-width que le min-width
@media (max-width: #{map-get($grid-breakpoints, md) - 0.02px}) {
    .modal.fade.show .modal-dialog {
        transform: translateY(0) !important;
        transition: transform 0.3s ease-out !important;
    }

    .modal.fade .modal-dialog {
        transform: translateY(100%) !important;
        transition: transform 0.3s ease-in !important;
    }

    .modal-dialog {
        margin: 0 auto !important;
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
        height: auto !important;
        max-height: calc(100vh - 110px) !important;
        overflow-y: auto !important;
    }

    .modal-dialog .modal-content {
        padding: 0 !important;
        height: auto !important;
    }

    .modal-content .modal-header {
        height: 2.8125rem !important;
    }

    .modal-content .modal-header .btn-close:not(.ob1-link-arrow) {
        display: block !important;
        margin: -0.9375rem -0.9375rem auto auto !important;
        height: 2.8125rem !important;
    }

    .modal-content .modal-header .btn-close::before {
        display: none !important;
    }

    .modal-content .modal-body {
        height: auto !important;
        min-height: auto !important;
        overflow-y: auto !important;
    }

    .modal-content .modal-footer {
        position: sticky !important;
        bottom: 0 !important;
        padding: 0.9375rem 0 !important;
        background-color: #fff !important;
    }
}

.ob1-link-icon .icon::before {
    font-size: 24px;
}

.modal-dialog .modal-header {
    position: static !important;
}

.modal-dialog .modal-header .btn-close {
    margin-top: 0 !important;
}
</style>
